import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

import type {
  EmergencyCommandOperation,
  EmergencyCommandResponse,
} from '@src/models/interfaces/emergency.interface';
import type { EmergencyCommandEnvelope } from '@src/packages/emergencies/api/commands';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

export const toFirestoreRecord = <T>(snapshot: QueryDocumentSnapshot<DocumentData>): T =>
  ({ id: snapshot.id, ...snapshot.data() }) as T;

export const executeEmergencyCommand = async <TPayload extends Record<string, unknown>>(
  organizationId: string,
  divisionId: string | undefined,
  operation: EmergencyCommandOperation,
  payload: TPayload,
): Promise<EmergencyCommandResponse> => {
  const callable = apiCallable<
    EmergencyCommandEnvelope<TPayload>,
    EmergencyCommandResponse
  >(API_OPERATIONS.EMERGENCY_COMMAND);
  const response = await callable({
    organizationId,
    ...(divisionId && { divisionId }),
    operation,
    idempotencyKey: globalThis.crypto.randomUUID(),
    occurredAtDevice: new Date(),
    payload,
  });
  return response.data;
};

const FIRESTORE_IN_QUERY_LIMIT = 10;

export const chunkEmergencyQueryValues = <T>(values: T[]): T[][] =>
  values.reduce<T[][]>((chunks, value, index) => {
    const chunkIndex = Math.floor(index / FIRESTORE_IN_QUERY_LIMIT);
    if (!chunks[chunkIndex]) chunks[chunkIndex] = [];
    chunks[chunkIndex].push(value);
    return chunks;
  }, []);
